/**
 * Criterion Card Component
 *
 * Displays a single criterion with expand/collapse functionality and drag-and-drop support.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, Edit2, Trash2, AlertTriangle, Eye, GripVertical } from 'lucide-react';
import type { Criterion, CriterionCategory, CATEGORY_LABELS } from '../../types';

interface CriterionCardProps {
  criterion: Criterion;
  onEdit: () => void;
  onDelete: () => void;
  categoryLabel: string;
  // Drag-and-drop props
  isDraggable?: boolean;
  isDragging?: boolean;
  isDragOver?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
}

export function CriterionCard({
  criterion,
  onEdit,
  onDelete,
  categoryLabel,
  isDraggable = false,
  isDragging = false,
  isDragOver = false,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
}: CriterionCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const isInclusion = criterion.type === 'inclusion';

  return (
    <div
      draggable={isDraggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`bg-white dark:bg-gray-800 rounded-xl border transition-all ${
        isInclusion ? 'border-green-200 dark:border-green-800' : 'border-red-200 dark:border-red-800'
      } ${isDragging ? 'opacity-50 scale-[0.98]' : ''} ${
        isDragOver ? 'border-2 border-primary-400 shadow-lg' : ''
      } overflow-hidden`}
    >
      {/* Header */}
      <div
        className={`px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
          isInclusion ? 'bg-green-50/50 dark:bg-green-900/20' : 'bg-red-50/50 dark:bg-red-900/20'
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isDraggable && (
          <div
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
            onClick={e => e.stopPropagation()}
          >
            <GripVertical size={18} />
          </div>
        )}
        <button className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>

        <span
          className={`font-mono text-sm font-medium ${
            isInclusion ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
          }`}
        >
          {criterion.criterion_number}
        </span>

        <span className="flex-1 text-sm text-gray-900 dark:text-gray-100 line-clamp-1">{criterion.criterion_text}</span>

        <div className="flex items-center gap-2">
          {criterion.is_critical && (
            <span className="text-amber-500 dark:text-amber-400" title="Critical criterion">
              <AlertTriangle size={16} />
            </span>
          )}
          {criterion.requires_manual_review && (
            <span className="text-primary-500 dark:text-primary-400" title="Requires manual review">
              <Eye size={16} />
            </span>
          )}
          <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-gray-600 dark:text-gray-400">
            {categoryLabel}
          </span>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 space-y-3">
          {/* Full criterion text */}
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Criterion Text</p>
            <p className="text-sm text-gray-900 dark:text-gray-100">{criterion.criterion_text}</p>
          </div>

          {/* Structured definition */}
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Structured Definition</p>
            <div className="bg-gray-50 dark:bg-gray-900/40 rounded p-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Target Type:</span>{' '}
                  <span className="text-gray-900 dark:text-gray-100">{criterion.structured_definition.target_type}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Target Value:</span>{' '}
                  <span className="text-gray-900 dark:text-gray-100">{criterion.structured_definition.target_value}</span>
                </div>
                {criterion.structured_definition.negation && (
                  <div className="col-span-2">
                    <span className="text-red-600 dark:text-red-400 font-medium">Negation: Looking for absence</span>
                  </div>
                )}
              </div>
              {criterion.structured_definition.constraints &&
                criterion.structured_definition.constraints.length > 0 && (
                  <div className="mt-2">
                    <span className="text-gray-500 dark:text-gray-400">Constraints:</span>
                    <ul className="mt-1 space-y-1">
                      {criterion.structured_definition.constraints.map((c, i) => (
                        <li key={i} className="text-gray-900 dark:text-gray-100">
                          {c.operator} {c.value} {c.unit}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>
          </div>

          {/* Search terms */}
          {criterion.search_terms && criterion.search_terms.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search Terms</p>
              <div className="flex flex-wrap gap-1">
                {criterion.search_terms.map((term, i) => (
                  <span key={i} className="text-xs bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 px-2 py-0.5 rounded">
                    {term}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            <button
              onClick={e => {
                e.stopPropagation();
                onEdit();
              }}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors min-h-[44px] sm:min-h-0"
            >
              <Edit2 size={14} />
              Edit
            </button>
            <button
              onClick={e => {
                e.stopPropagation();
                onDelete();
              }}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors min-h-[44px] sm:min-h-0"
            >
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
